import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { GetWorkspaceListReq, WorkspaceListReqType, WorkspaceNpcInfo } from '../types/workspaceNpc';
import { postGetWorkspaceBanner, postWorkspaceList } from '../api';
import { AppState } from '.';
import { RootState } from './rootReducers';

const DefaultSize = 20;

export const getWorkspaceList = createAsyncThunk(
  'home/getWorkspaceList',
  async (
    {
      params,
    }: {
      params: Pick<GetWorkspaceListReq, 'req_type'>;
    },
    { rejectWithValue, getState }
  ) => {
    try {
      const {
        home,
        user: { authData },
      } = getState() as AppState;
      const { max_create_time, page, size } = home[params.req_type];
      return await postWorkspaceList(
        {
          max_create_time,
          page,
          size,
          req_type: params.req_type,
        },
        { params: { authData } }
      );
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getWorkspaceBanner = createAsyncThunk(
  'home/getWorkspaceBanner',
  async (_, { rejectWithValue, getState }) => {
    try {
      const {
        user: { authData },
      } = getState() as RootState;
      return postGetWorkspaceBanner({}, { params: { authData } });
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

interface Entity {
  data: WorkspaceNpcInfo[];
  hasMore: boolean;
  loading: boolean;
  max_create_time: number;
  size: number;
  page: number;
}

export interface IHomeState {
  banners: any[];
  bannerLoading: boolean;
  currentTab: WorkspaceListReqType;
  [WorkspaceListReqType.All]: Entity;
  [WorkspaceListReqType.Draft]: Entity;
  [WorkspaceListReqType.InModeration]: Entity;
}

const initialState: IHomeState = {
  banners: [],
  bannerLoading: false,
  currentTab: WorkspaceListReqType.All,
  [WorkspaceListReqType.All]: {
    data: [],
    hasMore: true,
    loading: false,
    max_create_time: 0,
    size: DefaultSize,
    page: 0,
  },
  [WorkspaceListReqType.Draft]: {
    data: [],
    hasMore: true,
    loading: false,
    max_create_time: 0,
    size: DefaultSize,
    page: 0,
  },
  [WorkspaceListReqType.InModeration]: {
    data: [],
    hasMore: true,
    loading: false,
    max_create_time: 0,
    size: DefaultSize,
    page: 0,
  },
};

export const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    setCurrentTab: (state, action: PayloadAction<WorkspaceListReqType>) => {
      state.currentTab = action.payload;
    },
    deleteNpc: (state, action: PayloadAction<{ npcId: number }>) => {
      const { npcId } = action.payload;
      state[WorkspaceListReqType.All].data = state[WorkspaceListReqType.All].data.filter(
        (s) => s.npc_id !== npcId
      );
      state[WorkspaceListReqType.Draft].data = state[WorkspaceListReqType.Draft].data.filter(
        (s) => s.npc_id !== npcId
      );
      state[WorkspaceListReqType.InModeration].data = state[
        WorkspaceListReqType.InModeration
      ].data.filter((s) => s.npc_id !== npcId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWorkspaceList.pending, (state, action) => {
        state[action.meta.arg.params.req_type].loading = true;
      })
      .addCase(getWorkspaceList.fulfilled, (state, action) => {
        const type = action.meta.arg.params.req_type;
        state[type].data.push(...(action.payload.workspace_npc_info || []));
        state[type].hasMore = action.payload.has_more || false;
        state[type].max_create_time = action.payload.min_create_time;
        state[type].page += 1;
        state[type].loading = false;
      })
      .addCase(getWorkspaceList.rejected, (state, action) => {
        state[action.meta.arg.params.req_type].loading = false;
        state[action.meta.arg.params.req_type].hasMore = false;
      });

    builder
      .addCase(getWorkspaceBanner.pending, (state) => {
        state.bannerLoading = true;
      })
      .addCase(getWorkspaceBanner.fulfilled, (state, action) => {
        state.bannerLoading = false;
        state.banners = action.payload.banner_list;
      })
      .addCase(getWorkspaceBanner.rejected, (state) => {
        state.bannerLoading = false;
      });
  },
});

const { actions } = homeSlice;
export const { setCurrentTab, deleteNpc } = actions;
